import React from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import pt from 'date-fns/locale/pt-BR';
import { isSameDay } from 'date-fns';

import Button from '../../../../components/Button';
import ContainerModal from '../../../../components/ContainerModal';
import DatePickerHeader from '../DatePickerHeader';

import { ScheduleItemProps } from '../../../../models/Schedule';
import * as S from './styles';
import { ScheduleChannelsMapped } from '../../../../utils/enums/channels';
import { ItemToSchedule } from '../../../../contexts/ModuleContext';
import { FirebaseEvents } from '../../../../utils/enums/firebase-events';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { useServiceContext } from '../../../../contexts/ServiceContext';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { useSpecialistContext } from '../../../../contexts/SpecialistContext';

registerLocale('pt-BR', pt);

type DatePickerModalProps = {
  schedule?: ScheduleItemProps;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  displayDates: Date[] | undefined;
  setDisplayDates: (date: Date[]) => void;

  displayDatesPicker: Date[] | undefined;
  getSchedulesDatesByMonth: ({
    unitIdSGH,
    itemId,
    isKit,
    date,
    resourceId,
    changeMonth,
    localId
  }: {
    unitIdSGH: string;
    itemId: string;
    isKit: boolean;
    date: Date;
    resourceId: string | number | null;
    changeMonth?: boolean;
    localId: string;
  }) => Promise<void>;
  isOpen: boolean;
  setIsOpen: (visible: boolean) => void;
  selectedItemToSchedule: ItemToSchedule;
  loadSpecialistSlotsMonth: (initRangeDate: Date) => Promise<void>;
};

const DatePickerModal = ({
  schedule,
  selectedDate,
  setSelectedDate,
  displayDates,
  setDisplayDates,
  displayDatesPicker,
  getSchedulesDatesByMonth,
  isOpen,
  setIsOpen,
  selectedItemToSchedule,
  loadSpecialistSlotsMonth
}: DatePickerModalProps) => {
  const [waitingDates, setWaitingDates] = React.useState(false);

  const { selectedService } = useServiceContext();
  const { selectedItem, scheduleSpecialistSelected } = useScheduleContext();
  const { selectedSpecialist } = useSpecialistContext();

  const { logEvent } = useAnalytics();

  React.useEffect(() => {
    if (waitingDates) {
      setWaitingDates(false);

      logEvent({
        eventName: FirebaseEvents.schedule_calendar,
        eventParams: {
          service_id: selectedService.id,
          service_name: selectedService.name,
          ...(selectedItemToSchedule === 'SPECIALIST'
            ? {
                product_name: selectedSpecialist?.name,
                product_id: selectedSpecialist?.id,
                unit_id: scheduleSpecialistSelected?.unit?.catalogId,
                unit_name: scheduleSpecialistSelected?.unit?.name,
                attendanceType: scheduleSpecialistSelected?.attendanceType
              }
            : {}),
          ...(selectedItemToSchedule !== 'SPECIALIST'
            ? {
                product_name: selectedItem?.productName,
                product_id: selectedItem?.productId,
                unit_id: selectedItem?.unit?.catalogUnitId,
                unit_name: selectedItem?.unit?.name,
                attendanceType: selectedItem?.careType
              }
            : {}),
          date: selectedDate
        }
      });

      if (!displayDatesPicker?.length)
        logEvent({
          eventName: FirebaseEvents.schedule_empty_month,
          eventParams: {
            service_id: selectedService.id,
            service_name: selectedService.name,
            ...(selectedItemToSchedule === 'SPECIALIST'
              ? {
                  product_name: selectedSpecialist?.name,
                  product_id: selectedSpecialist?.id,
                  unit_id: scheduleSpecialistSelected?.unit?.catalogId,
                  unit_name: scheduleSpecialistSelected?.unit?.name,
                  attendanceType: scheduleSpecialistSelected?.attendanceType
                }
              : {}),
            ...(selectedItemToSchedule !== 'SPECIALIST'
              ? {
                  product_name: selectedItem?.productName,
                  product_id: selectedItem?.productId,
                  unit_id: selectedItem?.unit?.catalogUnitId,
                  unit_name: selectedItem?.unit?.name,
                  attendanceType: selectedItem?.careType
                }
              : {}),
            date: selectedDate
          }
        });
    }
  }, [displayDatesPicker]);

  const changeMonth = (date: Date) => {
    handleChangeMonth(date);
    setWaitingDates(true);
  };

  const firstDateWithSlot = displayDates?.length ? displayDates[0] : undefined;

  const handleSelectDate = (date: Date) => {
    if (displayDates?.length) {
      const displayDatesSummarized = displayDates?.slice(0, 3);
      if (!displayDatesSummarized.some(item => isSameDay(item, date))) {
        const displayDatesSlice = displayDates?.slice(0, 2);
        if (displayDatesSlice) setDisplayDates([...displayDatesSlice, date]);
      }
    }
    setSelectedDate(date);
  };

  const handleChangeMonth = (date: Date) => {
    if (selectedItemToSchedule === 'SPECIALIST') {
      loadSpecialistSlotsMonth(date);
    }
    if (schedule?.unitId && schedule?.itemId)
      if (
        schedule?.unitId !== ScheduleChannelsMapped.TELEORIENTATION &&
        schedule?.itemId !== ScheduleChannelsMapped.TELEORIENTATION
      )
        getSchedulesDatesByMonth({
          unitIdSGH: schedule.unitId,
          itemId: schedule.itemId,
          isKit: schedule.isKit || false,
          date: date,
          changeMonth: true,
          resourceId: schedule.useResouce ? schedule.resource?.id || null : null,
          localId: schedule.localId || ''
        });
    setSelectedDate(null);
  };

  return (
    <ContainerModal
      visible={isOpen}
      onClose={() => setIsOpen(false)}
      withoutCloseButton
      childrenFooter={
        <>
          <Button title="Pronto" onClick={() => setIsOpen(false)} />
          <S.ButtonClose tertiary title="Fechar" onClick={() => setIsOpen(false)} />
        </>
      }
    >
      <S.DatePickerWrapper>
        <DatePicker
          calendarClassName="datePicker"
          locale="pt-BR"
          selectsStart={false}
          openToDate={firstDateWithSlot}
          showPreviousMonths={false}
          renderCustomHeader={props => <DatePickerHeader {...props} initialDate={firstDateWithSlot} />}
          selected={selectedDate}
          onChange={handleSelectDate}
          inline
          includeDates={displayDatesPicker}
          onMonthChange={changeMonth}
          placeholderText="Selecione a data."
        />
      </S.DatePickerWrapper>
    </ContainerModal>
  );
};

export default DatePickerModal;
